import { useState, useEffect, useRef, useCallback } from "react";
import { Users, Crown, MessageSquare, UserPlus, X, RotateCcw, Eye, Clock } from "lucide-react";
import axios from "axios";

// ==================== WSOP-STYLE POKER GAME ====================

// Card Component - Large style for player's own cards
const Card = ({ card, faceDown, size = "normal" }) => {
  const sizeClasses = {
    tiny: "w-6 h-8 text-[8px]",
    small: "w-8 h-11 text-xs",
    normal: "w-12 h-16 text-sm",
    large: "w-20 h-28 text-2xl",
    xlarge: "w-24 h-36 text-3xl"
  };

  if (faceDown || !card) {
    return (
      <div className={`${sizeClasses[size]} rounded-lg bg-gradient-to-br from-blue-800 to-blue-900 border-2 border-blue-600 flex items-center justify-center shadow-lg`}
        style={{ 
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.03) 5px, rgba(255,255,255,0.03) 10px)'
        }}
      >
        <div className="text-blue-400 opacity-50">♠</div>
      </div>
    );
  }

  // Handle both string format ("K♥") and object format ({rank: "K", suit: "♥"})
  let suit, rank;
  if (typeof card === 'string') {
    suit = card.slice(-1);
    rank = card.slice(0, -1);
  } else if (typeof card === 'object' && card !== null) {
    suit = card.suit || '';
    rank = card.rank || '';
  } else {
    // Invalid card format - show face down
    return (
      <div className={`${sizeClasses[size]} rounded-lg bg-gradient-to-br from-blue-800 to-blue-900 border-2 border-blue-600 flex items-center justify-center shadow-lg`}>
        <div className="text-blue-400 opacity-50">?</div>
      </div>
    );
  }
  
  const isRed = suit === '♥' || suit === '♦';
  
  return (
    <div className={`${sizeClasses[size]} rounded-lg bg-white border border-gray-300 flex flex-col items-center justify-center font-bold shadow-lg relative overflow-hidden`}>
      <span className={isRed ? "text-red-600" : "text-gray-900"}>{rank}</span>
      <span className={`${isRed ? "text-red-600" : "text-gray-900"} ${size === 'large' || size === 'xlarge' ? 'text-4xl' : ''}`}>{suit}</span>
    </div>
  );
};

// Chip Stack Component
const ChipStack = ({ amount, small = false }) => {
  if (!amount || amount <= 0) return null;
  
  const formatAmount = (n) => {
    if (n >= 1000000) return (n/1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n/1000).toFixed(0) + 'K';
    return n.toString();
  };

  return (
    <div className={`flex items-center gap-1 ${small ? 'scale-75' : ''}`}>
      <div className="relative">
        {/* Chip stack visual */}
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 border-2 border-purple-300 flex items-center justify-center shadow-lg">
          <div className="w-5 h-5 rounded-full border border-purple-300 flex items-center justify-center">
            <span className="text-white text-[8px] font-bold">♦</span>
          </div>
        </div>
      </div>
      <span className="text-white font-bold text-sm drop-shadow-lg">{formatAmount(amount)}</span>
    </div>
  );
};

// Player Seat Component - Fixed Layout at CORNERS
const PlayerSeat = ({ player, isMe, isActive, isDealer, position }) => {
  if (!player) return null;

  const isBot = player.name === 'PokerBot';
  const isFolded = player.folded;
  
  // Fixed corner positions - absolute pixels from edges
  const positionStyles = {
    bottomLeft: "bottom-2 left-2",      // YOU - bottom left corner
    topLeft: "top-2 left-2",            // Player 2 - top left corner
    topRight: "top-2 right-2",          // Player 3 - top right corner  
    bottomRight: "bottom-2 right-2",    // Player 4 - bottom right corner
    topCenter: "top-2 left-1/2 -translate-x-1/2", // Player 5 - top center
    bottomCenter: "bottom-2 left-1/2 -translate-x-1/2" // Player 6 - bottom center
  };

  // Ring color based on status
  const ringColor = isFolded ? 'ring-gray-500' : isActive ? 'ring-green-400 ring-4 animate-pulse' : 'ring-blue-400';
  
  // Cards position - always to the RIGHT of the avatar, never on top
  const isLeftSide = position === 'bottomLeft' || position === 'topLeft';

  return (
    <div className={`absolute ${positionStyles[position]} z-20`}>
      <div className={`flex items-center gap-2 ${isLeftSide ? 'flex-row' : 'flex-row-reverse'}`}>
        {/* Avatar + Info Block */}
        <div className={`flex flex-col items-center ${isFolded ? 'opacity-50' : ''}`}>
          {/* Action indicator above avatar */}
          {player.lastAction && (
            <div className={`px-2 py-0.5 rounded text-[10px] font-bold text-white mb-1 ${
              player.lastAction === 'fold' ? 'bg-red-600' :
              player.lastAction === 'call' ? 'bg-green-600' :
              player.lastAction === 'raise' || player.lastAction === 'bet' ? 'bg-yellow-600' :
              player.lastAction === 'check' ? 'bg-blue-600' : 'bg-gray-600'
            }`}>
              {player.lastAction.toUpperCase()}
            </div>
          )}

          {/* Avatar */}
          <div className={`w-12 h-12 rounded-full ring-2 ${ringColor} overflow-hidden bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center shadow-xl`}>
            {isBot ? (
              <span className="text-xl">🤖</span>
            ) : player.gender === 'female' ? (
              <span className="text-xl">👩</span>
            ) : (
              <span className="text-xl">👨</span>
            )}
          </div>

          {/* Player name + chips below avatar */}
          <div className="mt-1 text-center">
            <div className={`text-[10px] font-bold ${isMe ? 'text-green-400' : 'text-white'} whitespace-nowrap`}>
              {isMe ? '★ YOU ★' : player.name}
            </div>
            <div className="text-yellow-400 text-[10px] font-bold">
              💰{player.chips?.toLocaleString()}
            </div>
            {player.currentBet > 0 && (
              <div className="text-orange-400 text-[9px]">
                Bet: {player.currentBet}
              </div>
            )}
          </div>
        </div>

        {/* Cards - TO THE SIDE, never covering */}
        {player.cards?.length > 0 && (
          <div className="flex gap-0.5">
            <Card card={isMe || player.showCards || player.revealCards ? player.cards[0] : null} faceDown={!isMe && !player.showCards && !player.revealCards} size={isMe ? "small" : "tiny"} />
            <Card card={isMe || player.showCards || player.revealCards ? player.cards[1] : null} faceDown={!isMe && !player.showCards && !player.revealCards} size={isMe ? "small" : "tiny"} />
          </div>
        )}
      </div>
    </div>
  );
};

// Dealer Avatar - at 12:00 position (TOP CENTER)
const DealerAvatar = ({ phase }) => {
  return (
    <div className="absolute top-0 left-1/2 -translate-x-1/2 z-30 bg-gradient-to-b from-yellow-600 to-yellow-800 rounded-b-lg px-4 py-1 border-2 border-yellow-400 border-t-0">
      <div className="flex items-center gap-2">
        <span className="text-lg">👩‍💼</span>
        <div className="flex flex-col">
          <span className="text-white text-[10px] font-bold">DEALER</span>
          <span className="text-yellow-200 text-[9px]">{phase?.toUpperCase() || 'WAITING'}</span>
        </div>
      </div>
    </div>
  );
};

// Main Poker Component
const MultiplayerPoker = ({ roomId, user, token, API, friends: propFriends = [] }) => {
  // State
  const [view, setView] = useState("lobby");
  const [tables, setTables] = useState([]);
  const [currentTable, setCurrentTable] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [betAmount, setBetAmount] = useState(0);
  const [joinCode, setJoinCode] = useState("");
  const [message, setMessage] = useState("");
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [practiceGame, setPracticeGame] = useState(null);
  const [botThinking, setBotThinking] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showFriends, setShowFriends] = useState(false);
  const [friends, setFriends] = useState(propFriends);
  const [isSpectator, setIsSpectator] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  const wsRef = useRef(null);
  const practiceTimerRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // WebSocket URL - safely handle API being undefined
  // Keep /api in the path since poker websocket is at /api/ws/poker/
  const WS_URL = API ? API.replace('https://', 'wss://').replace('http://', 'ws://') : '';

  // Fetch friends list
  const fetchFriends = useCallback(async () => {
    if (!API || !token) return;
    try {
      const res = await axios.get(`${API}/friends/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriends(res.data.friends || []);
    } catch (err) {
      console.error("Failed to fetch friends:", err);
    }
  }, [API, token]);

  useEffect(() => {
    if (showFriends) {
      fetchFriends();
    }
  }, [showFriends, fetchFriends]);

  // Fetch tables
  const fetchTables = useCallback(async () => {
    if (!API || !token) {
      console.warn("Poker: Missing API or token");
      return;
    }
    try {
      const res = await axios.get(`${API}/poker/tables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTables(res.data.tables || []);
    } catch (err) {
      console.error("Failed to fetch tables:", err);
    }
  }, [API, token]);

  useEffect(() => {
    fetchTables();
    const interval = setInterval(fetchTables, 5000);
    return () => clearInterval(interval);
  }, [fetchTables]);

  // Connect to WebSocket
  const connectWs = useCallback((tableCode, pId, spectatorMode = false) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Clear any existing timer interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    const wsPath = spectatorMode 
      ? `${WS_URL}/ws/poker/${tableCode}/spectate?token=${token}`
      : `${WS_URL}/ws/poker/${tableCode}?player_id=${pId}&token=${token}`;
    
    const ws = new WebSocket(wsPath);
    
    // Keep-alive ping interval
    let pingInterval = null;
    
    ws.onopen = () => {
      console.log(`Poker WS connected (${spectatorMode ? 'spectator' : 'player'})`);
      setMessage("");
      setIsSpectator(spectatorMode);
      
      // Start ping interval to keep connection alive
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 25000); // Ping every 25 seconds
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log("Poker WS received:", data.type, data);
      
      if (data.type === 'game_state') {
        setGameState(data.state);
        // Update time remaining from server
        if (data.state.timeRemaining !== null && data.state.timeRemaining !== undefined) {
          setTimeRemaining(data.state.timeRemaining);
        }
        console.log("Game state updated:", data.state.players?.length, "players");
      } else if (data.type === 'player_joined') {
        setMessage(`${data.name} joined! (${data.player_count} players)`);
        console.log("Player joined:", data.name, "Total:", data.player_count);
      } else if (data.type === 'player_left') {
        setMessage(`${data.name} left the table`);
        // If we're waiting and alone, show waiting message
        toast.info(`${data.name} has left the table`);
      } else if (data.type === 'spectator_joined') {
        setMessage(`👁️ ${data.username} is now watching`);
      } else if (data.type === 'spectator_left') {
        setMessage(`👁️ ${data.username} stopped watching`);
      } else if (data.type === 'timeout_fold') {
        setMessage(`⏰ ${data.player} timed out and folded!`);
      } else if (data.type === 'error') {
        setMessage(data.message);
      }
    };

    ws.onclose = (event) => {
      console.log("Poker WS closed", event.code, event.reason);
      // Clear ping interval
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      // If connection was closed unexpectedly, try to reconnect
      if (event.code !== 1000 && event.code !== 1005) { // 1000 = normal, 1005 = no status
        setMessage("Connection lost - attempting to reconnect...");
        // Try to reconnect after a short delay
        const reconnectTimeout = setTimeout(() => {
          if (currentTable?.code && playerId) {
            console.log("Attempting poker reconnect...");
            connectWs(currentTable.code, playerId, isSpectator);
          } else {
            // Can't reconnect, return to lobby
            setMessage("Could not reconnect - returning to lobby");
            setTimeout(() => {
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
              }
              setView("lobby");
              setCurrentTable(null);
              setGameState(null);
              setPlayerId(null);
              setIsSpectator(false);
              setTimeRemaining(null);
              fetchTables();
            }, 2000);
          }
        }, 3000);
        
        return () => clearTimeout(reconnectTimeout);
      }
    };

    ws.onerror = (error) => {
      console.error("Poker WS error:", error);
      // Clear ping interval on error
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      setMessage("Connection error - please try again");
    };

    wsRef.current = ws;

    // Start local timer countdown for smoother UI
    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null || prev <= 0) return prev;
        return prev - 1;
      });
    }, 1000);
  }, [WS_URL, token]);

  // Watch table as spectator
  const watchTable = (tableCode) => {
    setCurrentTable({ code: tableCode });
    setView("game");
    connectWs(tableCode, null, true);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Create table
  const createTable = async () => {
    try {
      const res = await axios.post(`${API}/poker/tables/create`, {
        name: `${user.username}'s Table`,
        max_players: 6,
        big_blind: 20,
        buy_in: 0
      }, { headers: { Authorization: `Bearer ${token}` }});

      setCurrentTable(res.data.table);
      setPlayerId(res.data.player_id);
      setView("game");
      connectWs(res.data.table.code, res.data.player_id);
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to create table");
    }
  };

  // Join table
  const joinTable = async (code) => {
    try {
      const res = await axios.post(`${API}/poker/tables/join`, { code }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCurrentTable(res.data.table);
      setPlayerId(res.data.player_id);
      setView("game");
      connectWs(res.data.table.code, res.data.player_id);
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to join table");
    }
  };

  // Leave table
  const leaveTable = async () => {
    if (wsRef.current) {
      if (!isSpectator) {
        wsRef.current.send(JSON.stringify({ action: 'leave' }));
      }
      wsRef.current.close();
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    setView("lobby");
    setCurrentTable(null);
    setGameState(null);
    setPlayerId(null);
    setIsPracticeMode(false);
    setPracticeGame(null);
    setIsSpectator(false);
    setTimeRemaining(null);
    fetchTables();
  };

  // Game actions
  const sendAction = (action, amount = 0) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      if (action === 'deal') {
        // Deal uses a different message type
        wsRef.current.send(JSON.stringify({ type: 'new_hand' }));
      } else {
        // Other actions (fold, call, check, raise)
        wsRef.current.send(JSON.stringify({ type: 'action', action, amount }));
      }
    }
  };

  // Poll for game state (fallback if WebSocket isn't updating)
  useEffect(() => {
    if (!currentTable || isPracticeMode) return;
    
    const pollGameState = async () => {
      try {
        const res = await axios.get(`${API}/poker/tables/${currentTable.code}/state`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.state) {
          setGameState(res.data.state);
          console.log("Polled game state:", res.data.player_count, "players");
        }
      } catch (err) {
        console.error("Failed to poll game state:", err);
      }
    };
    
    // Poll every 3 seconds as a fallback
    const pollInterval = setInterval(pollGameState, 3000);
    
    return () => clearInterval(pollInterval);
  }, [currentTable, isPracticeMode, API, token]);

  // ==================== PRACTICE MODE ====================
  const startPractice = () => {
    setIsPracticeMode(true);
    setView("game");
    initPracticeGame();
  };

  const initPracticeGame = () => {
    const deck = createDeck();
    const playerCards = [deck.pop(), deck.pop()];
    const botCards = [deck.pop(), deck.pop()];
    
    setPracticeGame({
      deck,
      phase: 'preflop',
      pot: 30,
      communityCards: [],
      players: [
        { id: 'player', name: user?.username || 'You', chips: 1000, cards: playerCards, currentBet: 10, folded: false, gender: 'male' },
        { id: 'bot', name: 'PokerBot', chips: 1000, cards: botCards, currentBet: 20, folded: false, isBot: true }
      ],
      currentPlayer: 'player',
      minBet: 20,
      lastRaise: 20,
      handOver: false,
      winner: null,
      showCards: false
    });
  };

  const createDeck = () => {
    const suits = ['♠', '♥', '♦', '♣'];
    const ranks = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
    const deck = [];
    for (const suit of suits) {
      for (const rank of ranks) {
        deck.push(rank + suit);
      }
    }
    // Shuffle
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    return deck;
  };

  // Practice game actions
  const practiceAction = (action, amount = 0) => {
    if (!practiceGame || practiceGame.handOver) return;

    setPracticeGame(prev => {
      const game = { ...prev };
      const player = game.players.find(p => p.id === 'player');
      const bot = game.players.find(p => p.id === 'bot');
      
      if (action === 'fold') {
        player.folded = true;
        player.lastAction = 'fold';
        game.handOver = true;
        game.winner = 'bot';
        game.showCards = true;
      } else if (action === 'call') {
        const callAmount = game.minBet - player.currentBet;
        player.chips -= callAmount;
        player.currentBet = game.minBet;
        player.lastAction = 'call';
        game.pot += callAmount;
        game.currentPlayer = 'bot';
      } else if (action === 'check') {
        player.lastAction = 'check';
        game.currentPlayer = 'bot';
      } else if (action === 'raise') {
        const raiseAmount = amount || game.minBet * 2;
        const totalBet = game.minBet + raiseAmount;
        const needed = totalBet - player.currentBet;
        player.chips -= needed;
        player.currentBet = totalBet;
        player.lastAction = 'raise';
        game.pot += needed;
        game.minBet = totalBet;
        game.lastRaise = raiseAmount;
        game.currentPlayer = 'bot';
      } else if (action === 'allin') {
        const allInAmount = player.chips;
        player.currentBet += allInAmount;
        game.pot += allInAmount;
        player.chips = 0;
        player.lastAction = 'all-in';
        game.currentPlayer = 'bot';
      }

      return game;
    });

    // Bot takes turn after player
    if (action !== 'fold') {
      setTimeout(() => botTurn(), 1500);
    }
  };

  const botTurn = () => {
    setBotThinking(true);
    
    setTimeout(() => {
      setPracticeGame(prev => {
        if (!prev || prev.handOver || prev.currentPlayer !== 'bot') return prev;
        
        const game = { ...prev };
        const bot = game.players.find(p => p.id === 'bot');
        const player = game.players.find(p => p.id === 'player');
        
        // Simple bot AI
        const rand = Math.random();
        const callAmount = game.minBet - bot.currentBet;
        
        if (callAmount === 0) {
          // Can check
          if (rand < 0.6) {
            bot.lastAction = 'check';
          } else {
            const raiseAmt = Math.min(game.minBet, bot.chips);
            bot.chips -= raiseAmt;
            bot.currentBet += raiseAmt;
            game.pot += raiseAmt;
            game.minBet = bot.currentBet;
            bot.lastAction = 'raise';
          }
        } else {
          // Must call or fold
          if (rand < 0.3 && callAmount > bot.chips / 4) {
            bot.folded = true;
            bot.lastAction = 'fold';
            game.handOver = true;
            game.winner = 'player';
            game.showCards = true;
          } else {
            const actualCall = Math.min(callAmount, bot.chips);
            bot.chips -= actualCall;
            bot.currentBet += actualCall;
            game.pot += actualCall;
            bot.lastAction = 'call';
          }
        }
        
        // Advance phase if both players have matched
        if (!game.handOver && bot.currentBet === player.currentBet) {
          advancePhase(game);
        }
        
        game.currentPlayer = 'player';
        return game;
      });
      
      setBotThinking(false);
    }, 1000);
  };

  const advancePhase = (game) => {
    const phases = ['preflop', 'flop', 'turn', 'river', 'showdown'];
    const currentIdx = phases.indexOf(game.phase);
    
    if (currentIdx < phases.length - 1) {
      game.phase = phases[currentIdx + 1];
      
      // Deal community cards
      if (game.phase === 'flop') {
        game.communityCards = [game.deck.pop(), game.deck.pop(), game.deck.pop()];
      } else if (game.phase === 'turn' || game.phase === 'river') {
        game.communityCards.push(game.deck.pop());
      } else if (game.phase === 'showdown') {
        game.handOver = true;
        // Show all cards at showdown
        game.players.forEach(p => p.showCards = true);
        game.winner = Math.random() > 0.5 ? 'player' : 'bot';
      }
      
      // Reset bets for new round
      game.players.forEach(p => p.currentBet = 0);
      game.minBet = 0;
    }
  };

  // ==================== RENDER ====================
  const currentGame = isPracticeMode ? practiceGame : gameState;
  const isHost = currentTable?.host === user?.username;
  const myPlayer = currentGame?.players?.find(p => isPracticeMode ? p.id === 'player' : p.odyn === playerId);
  const canAct = currentGame && !currentGame.handOver && (isPracticeMode ? currentGame.currentPlayer === 'player' : currentGame.currentPlayer === playerId);
  const canDeal = !isPracticeMode && isHost && (gameState?.phase === 'waiting' || gameState?.phase === 'showdown') && (gameState?.players?.length || 0) >= 2;

  // Get positioned players at CORNERS - You at bottom-left, others clockwise
  const getPositionedPlayers = () => {
    if (!currentGame?.players) return [];
    // 4 corners: bottomLeft (you), topLeft, topRight, bottomRight
    // If more than 4 players, add topCenter and bottomCenter
    const positions = ['bottomLeft', 'topLeft', 'topRight', 'bottomRight', 'topCenter', 'bottomCenter'];
    const players = [...currentGame.players];
    
    // Put current player (me) first - will be at bottomLeft
    const myIdx = players.findIndex(p => isPracticeMode ? p.id === 'player' : p.odyn === playerId);
    if (myIdx > 0) {
      const me = players.splice(myIdx, 1)[0];
      players.unshift(me);
    }
    
    return players.map((p, i) => ({
      ...p,
      position: positions[i % positions.length]
    }));
  };

  // ==================== LOBBY VIEW ====================
  if (view === "lobby") {
    return (
      <div className="h-full bg-gradient-to-b from-gray-900 via-blue-900 to-gray-900 rounded-xl p-4 overflow-auto">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-1">♠ TEXAS HOLD'EM ♥</h2>
            <p className="text-yellow-400 text-sm">F-Chips: {user?.fchips_balance?.toLocaleString() || '1,000'}</p>
          </div>

          {/* Practice Mode Button */}
          <button
            onClick={startPractice}
            className="w-full py-3 mb-4 bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-500 hover:to-indigo-600 rounded-lg text-white font-bold flex items-center justify-center gap-2 shadow-lg text-lg"
          >
            🤖 PRACTICE vs BOT
          </button>

          {/* Create/Join */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              onClick={createTable}
              className="py-3 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600 rounded-lg text-white font-bold shadow-lg"
            >
              + Create Table
            </button>
            <div className="flex gap-1">
              <input
                type="text"
                placeholder="Code"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="flex-1 px-2 py-2 rounded-lg bg-gray-800 text-white border border-gray-600 text-center font-mono"
                maxLength={6}
              />
              <button
                onClick={() => joinTable(joinCode)}
                disabled={joinCode.length !== 6}
                className="px-4 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 rounded-lg text-white font-bold"
              >
                Join
              </button>
            </div>
          </div>

          {message && (
            <div className="p-2 mb-4 bg-red-500/20 border border-red-500 rounded text-red-300 text-center text-sm">
              {message}
            </div>
          )}

          {/* Tables List */}
          <div className="space-y-2">
            <h3 className="text-white font-bold text-sm flex items-center gap-2">
              <Users className="w-4 h-4" /> Active Tables
            </h3>
            {tables.length === 0 ? (
              <p className="text-gray-400 text-center py-4 text-sm">No tables. Create one or practice vs bot!</p>
            ) : (
              tables.map(t => (
                <div
                  key={t.code}
                  className="p-3 bg-gray-800/50 rounded-lg border border-gray-700"
                >
                  <div className="flex justify-between items-center">
                    <span className="text-white font-bold">{t.name}</span>
                    <span className="text-xs text-gray-400 font-mono">{t.code}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-gray-400 text-xs">
                      {t.player_count}/{t.max_players} players
                    </span>
                    {t.spectator_count > 0 && (
                      <span className="text-purple-400 text-xs flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {t.spectator_count} watching
                      </span>
                    )}
                    <span className="text-gray-500 text-xs">• BB: {t.big_blind}</span>
                    {t.phase && t.phase !== 'waiting' && (
                      <span className="text-green-400 text-xs animate-pulse">• IN PROGRESS</span>
                    )}
                  </div>
                  {/* Show who's playing */}
                  {t.players && t.players.length > 0 && (
                    <div className="text-gray-500 text-xs mt-1">
                      Playing: {t.players.map(p => p.name).join(', ')}
                    </div>
                  )}
                  {/* Action buttons */}
                  <div className="flex gap-2 mt-2">
                    {t.player_count < t.max_players ? (
                      <button
                        onClick={() => joinTable(t.code)}
                        className="flex-1 py-2 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600 rounded text-white font-bold text-xs"
                      >
                        🎮 JOIN GAME
                      </button>
                    ) : (
                      <button
                        disabled
                        className="flex-1 py-2 bg-gray-600 rounded text-gray-400 font-bold text-xs cursor-not-allowed"
                      >
                        TABLE FULL
                      </button>
                    )}
                    <button
                      onClick={() => watchTable(t.code)}
                      className="flex-1 py-2 bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-500 hover:to-indigo-600 rounded text-white font-bold text-xs flex items-center justify-center gap-1"
                    >
                      <Eye className="w-3 h-3" /> WATCH
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==================== GAME VIEW - FIXED CORNER LAYOUT ====================
  return (
    <div className="h-full bg-gradient-to-b from-gray-900 via-blue-900 to-gray-900 rounded-xl flex flex-col overflow-hidden relative">
      {/* PROMINENT WIN/LOSE BANNER - Always visible when game over */}
      {(currentGame?.handOver || currentGame?.phase === 'showdown') && (
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 px-8 py-4 rounded-xl shadow-2xl text-center ${
          (isPracticeMode ? currentGame?.winner === 'player' : (currentGame?.winner === playerId || currentGame?.winner === user?.username))
            ? 'bg-gradient-to-r from-green-600 to-emerald-500 border-4 border-yellow-400'
            : 'bg-gradient-to-r from-red-600 to-red-700 border-4 border-red-400'
        }`}>
          <div className="text-4xl font-black text-white drop-shadow-lg">
            {(isPracticeMode ? currentGame?.winner === 'player' : (currentGame?.winner === playerId || currentGame?.winner === user?.username))
              ? '🎉 YOU WIN! 🎉'
              : `😢 YOU LOSE 😢`}
          </div>
          <div className="text-xl text-yellow-300 font-bold mt-2">
            POT: {(currentGame?.pot || 0).toLocaleString()} chips
          </div>
        </div>
      )}

      {/* Top Bar - Compact with F-Chips balance */}
      <div className="flex items-center justify-between px-2 py-1 bg-black/50">
        <button
          onClick={leaveTable}
          className="px-2 py-1 bg-red-600 hover:bg-red-500 rounded text-white text-xs font-bold flex items-center gap-1"
        >
          <X className="w-3 h-3" /> EXIT
        </button>
        
        <div className="flex items-center gap-3">
          <div className="text-white font-bold text-sm">
            {isPracticeMode ? '🤖 PRACTICE' : `${currentTable?.code}`}
          </div>
          {isSpectator ? (
            <div className="bg-purple-600/80 px-2 py-0.5 rounded text-white text-xs font-bold flex items-center gap-1">
              <Eye className="w-3 h-3" /> SPECTATOR
            </div>
          ) : (
            <div className="bg-yellow-600/80 px-2 py-0.5 rounded text-white text-xs font-bold">
              🎰 {(myPlayer?.chips || 0).toLocaleString()} chips
            </div>
          )}
          {/* Timer display */}
          {timeRemaining !== null && timeRemaining > 0 && currentGame?.phase && !['waiting', 'showdown'].includes(currentGame.phase) && (
            <div className={`px-2 py-0.5 rounded text-white text-xs font-bold flex items-center gap-1 ${
              timeRemaining <= 10 ? 'bg-red-600 animate-pulse' : timeRemaining <= 20 ? 'bg-orange-600' : 'bg-green-600'
            }`}>
              <Clock className="w-3 h-3" /> {timeRemaining}s
            </div>
          )}
          {/* Spectator count */}
          {currentGame?.spectatorCount > 0 && (
            <div className="bg-purple-600/50 px-2 py-0.5 rounded text-purple-200 text-xs flex items-center gap-1">
              <Eye className="w-3 h-3" /> {currentGame.spectatorCount}
            </div>
          )}
        </div>

        <div className="flex gap-1">
          <button
            onClick={() => setShowChat(!showChat)}
            className={`p-1 rounded ${showChat ? 'bg-blue-600' : 'bg-gray-700'} text-white`}
          >
            <MessageSquare className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Main Game Area - Players at CORNERS */}
      <div className="flex-1 relative p-4">
        {/* Dealer at 12:00 (top center) */}
        <DealerAvatar phase={currentGame?.phase} />

        {/* Players at CORNERS */}
        {getPositionedPlayers().map((player, idx) => (
          <PlayerSeat
            key={player.odyn || player.id}
            player={player}
            isMe={isPracticeMode ? player.id === 'player' : player.odyn === playerId}
            isActive={isPracticeMode ? currentGame?.currentPlayer === player.id : currentGame?.currentPlayer === player.odyn}
            isDealer={false}
            position={player.position}
          />
        ))}

        {/* THE TABLE - Centered oval */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/5 h-2/3 rounded-[50%] bg-gradient-to-b from-green-700 to-green-900 border-8 border-amber-600 shadow-[0_0_40px_rgba(0,0,0,0.5),inset_0_0_40px_rgba(0,0,0,0.3)]">
          {/* Table felt pattern */}
          <div className="absolute inset-0 rounded-[50%] opacity-30"
            style={{
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 20px)'
            }}
          />

          {/* Community Cards + Pot in Center */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
            {/* Community Cards */}
            <div className="flex gap-1 mb-2">
              {[0, 1, 2, 3, 4].map(i => {
                const card = currentGame?.communityCards?.[i] || currentGame?.community_cards?.[i];
                return (
                  <Card 
                    key={i} 
                    card={card} 
                    faceDown={!card && currentGame?.phase !== 'waiting' && currentGame?.phase !== 'preflop'} 
                    size="small"
                  />
                );
              })}
            </div>

            {/* Pot */}
            {(currentGame?.pot > 0) && (
              <div className="bg-black/60 px-3 py-1 rounded-full">
                <span className="text-yellow-400 font-bold text-sm">POT: {currentGame.pot.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Status indicator - bottom center */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/80 rounded text-center z-30">
          {isSpectator ? (
            <span className="text-purple-400 font-bold flex items-center gap-2">
              <Eye className="w-4 h-4" /> Watching Game
              {currentGame?.spectatorCount > 1 && ` (${currentGame.spectatorCount} viewers)`}
            </span>
          ) : currentGame?.handOver ? null : botThinking ? (
            <span className="text-blue-400 font-bold">🤖 Bot thinking...</span>
          ) : canAct ? (
            <div className="flex flex-col items-center">
              <span className="text-green-400 font-bold animate-pulse">⚡ YOUR TURN ⚡</span>
              {timeRemaining !== null && timeRemaining > 0 && (
                <span className={`text-sm font-bold mt-1 ${
                  timeRemaining <= 10 ? 'text-red-400 animate-pulse' : timeRemaining <= 20 ? 'text-orange-400' : 'text-white'
                }`}>
                  ⏱️ {timeRemaining}s remaining
                </span>
              )}
            </div>
          ) : canDeal ? (
            <span className="text-green-400 font-bold">✓ Click DEAL to start</span>
          ) : currentGame?.phase === 'waiting' && (currentGame?.players?.length || 0) < 2 ? (
            <span className="text-yellow-400 font-bold">Waiting... ({currentGame?.players?.length}/2)</span>
          ) : (
            <span className="text-gray-400">Waiting...</span>
          )}
        </div>
      </div>

      {/* ACTION BUTTONS - Bottom */}
      <div className="bg-gray-900 border-t border-gray-700 p-2">
        {isSpectator ? (
          <div className="text-center py-4">
            <div className="text-purple-400 font-bold flex items-center justify-center gap-2">
              <Eye className="w-4 h-4" /> You are watching this game
            </div>
            <p className="text-gray-500 text-xs mt-1">You can chat with the players using the chat panel</p>
          </div>
        ) : isPracticeMode && practiceGame?.handOver ? (
          <button
            onClick={initPracticeGame}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600 rounded-lg text-white font-bold text-lg flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" /> DEAL NEW HAND
          </button>
        ) : currentGame?.handOver ? (
          <button
            onClick={() => sendAction('deal')}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600 rounded-lg text-white font-bold text-lg flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" /> DEAL NEW HAND
          </button>
        ) : canDeal ? (
          <button
            onClick={() => sendAction('deal')}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600 rounded-lg text-white font-bold text-lg"
          >
            🃏 DEAL
          </button>
        ) : canAct ? (
          <div className="space-y-2">
            {/* Timer warning */}
            {timeRemaining !== null && timeRemaining <= 10 && timeRemaining > 0 && (
              <div className="text-center text-red-400 font-bold animate-pulse text-sm">
                ⚠️ HURRY! {timeRemaining}s left or you'll auto-fold!
              </div>
            )}
            {/* Main action buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => isPracticeMode ? practiceAction('fold') : sendAction('fold')}
                className="flex-1 py-3 bg-gradient-to-b from-red-700 to-red-900 hover:from-red-600 hover:to-red-800 rounded-lg text-white font-bold text-sm border border-red-600 shadow-lg"
              >
                FOLD
              </button>
              <button
                onClick={() => isPracticeMode ? practiceAction('fold') : sendAction('fold_show')}
                className="py-3 px-2 bg-gradient-to-b from-orange-600 to-orange-800 hover:from-orange-500 hover:to-orange-700 rounded-lg text-white font-bold text-xs border border-orange-500 shadow-lg"
                title="Fold but show your cards to the table"
              >
                FOLD & SHOW
              </button>
              
              {(currentGame?.currentBet || 0) <= (myPlayer?.currentBet || 0) ? (
                <button
                  onClick={() => isPracticeMode ? practiceAction('check') : sendAction('check')}
                  className="flex-1 py-3 bg-gradient-to-b from-blue-700 to-blue-900 hover:from-blue-600 hover:to-blue-800 rounded-lg text-white font-bold text-sm border border-blue-600 shadow-lg"
                >
                  CHECK
                </button>
              ) : (
                <button
                  onClick={() => isPracticeMode ? practiceAction('call') : sendAction('call')}
                  className="flex-1 py-3 bg-gradient-to-b from-green-700 to-green-900 hover:from-green-600 hover:to-green-800 rounded-lg text-white font-bold text-sm border border-green-600 shadow-lg"
                >
                  CALL {((currentGame?.currentBet || 0) - (myPlayer?.currentBet || 0)).toLocaleString()}
                </button>
              )}
            </div>
            
            {/* Raise amount selection */}
            <div className="flex gap-1 items-center bg-gray-800 rounded-lg p-2">
              <span className="text-yellow-400 font-bold text-xs mr-2">RAISE:</span>
              {[10, 25, 50, 100, 250].map(amount => (
                <button
                  key={amount}
                  onClick={() => isPracticeMode ? practiceAction('raise', amount) : sendAction('raise', amount)}
                  disabled={amount > (myPlayer?.chips || 0)}
                  className={`px-3 py-2 rounded font-bold text-xs ${
                    amount > (myPlayer?.chips || 0)
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-b from-yellow-600 to-yellow-800 hover:from-yellow-500 hover:to-yellow-700 text-white border border-yellow-500'
                  }`}
                >
                  {amount}
                </button>
              ))}
              <button
                onClick={() => isPracticeMode ? practiceAction('raise', myPlayer?.chips || 0) : sendAction('raise', myPlayer?.chips || 0)}
                className="px-3 py-2 rounded font-bold text-xs bg-gradient-to-b from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white border border-red-500"
              >
                ALL IN
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-400">
            {isPracticeMode ? 
              (botThinking ? '🤖 Bot is thinking...' : 'Waiting...') : 
              (currentGame?.phase === 'waiting' ? 
                (isHost ? 'Waiting for players to join...' : 'Waiting for host to deal...') : 
                'Waiting for other players...'
              )
            }
          </div>
        )}
      </div>

      {/* Side Panels */}
      {showChat && (
        <div className="absolute right-0 top-12 bottom-20 w-64 bg-gray-900/95 border-l border-gray-700 p-3 overflow-auto">
          <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Table Chat
          </h3>
          <p className="text-gray-500 text-xs">Chat coming soon...</p>
        </div>
      )}

      {showFriends && (
        <div className="absolute left-0 top-12 bottom-20 w-64 bg-gray-900/95 border-r border-gray-700 p-3 overflow-auto">
          <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Invite Friends
          </h3>
          
          {/* Table code for easy sharing */}
          {currentTable?.code && (
            <div className="mb-3 p-2 bg-gray-800 rounded">
              <p className="text-gray-400 text-xs mb-1">Share this code:</p>
              <div className="text-yellow-400 font-mono text-lg font-bold text-center">{currentTable.code}</div>
            </div>
          )}
          
          {friends.length === 0 ? (
            <div className="text-gray-500 text-xs text-center py-4">
              <p>No friends online</p>
              <p className="mt-2 text-gray-600">Share the table code above!</p>
            </div>
          ) : (
            <div className="space-y-1">
              {friends.map(f => (
                <div key={f.user_id || f.username} className="p-2 hover:bg-gray-800 rounded flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                      {f.username?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <span className="text-white text-sm font-bold">{f.username}</span>
                      {f.status === 'online' && (
                        <span className="ml-1 w-2 h-2 bg-green-500 rounded-full inline-block"></span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      // TODO: Send invite notification
                      setMessage(`Invite sent to ${f.username}!`);
                      setTimeout(() => setMessage(''), 2000);
                    }}
                    className="px-2 py-1 bg-green-600 hover:bg-green-500 rounded text-white text-xs font-bold"
                  >
                    Invite
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiplayerPoker;
